import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SelectedPanelEnum } from '@app/core/enums/selected-panel.enum';
import { BreadcrumbService } from '@app/core/services/breadcrumb.service';
import { InsurancesQueryParamsInterface } from '@app/my-zone/interfaces/insurances-query-params.interface';
import * as layoutActions from '@app/store/actions/layout.actions';
import * as fromRoot from '@app/store/app.reducer';
import * as productPanelActions from '@app/store/my-zone/product-panel/product-panel.actions';
import * as scenariosActions from '@app/store/my-zone/scenarios/scenarios.actions';
import { Store } from '@ngrx/store';
import {
    ErrorInterface,
    InsurancePolicyActionService,
    InsurancePolicyDataService,
    InsurancePolicyLoadingService,
    InsurancePolicyLoadingStrategyEnum,
    PurchasedProductInterface,
    RelationInterface,
    RelationDataService,
    DatalayerService,
} from 'outshared-lib';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { PolicyProductInterface } from 'outshared-lib/analytics/services/datalayer/policy-product.interface';

/** @deprecated - insurances functionality is moved to libs/my-zone/insurances */
@Component({
    selector: 'ins-insurances-container',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <ins-insurances
            [purchasedProducts]="purchasedProducts$ | async"
            [total]="total$ | async"
            [loading]="loading$ | async"
            [relation]="relation$ | async"
        >
        </ins-insurances>
    `,
})
export class InsurancesContainer implements OnInit, OnDestroy {
    public purchasedProducts$: Observable<PurchasedProductInterface[]>;
    public total$: Observable<number>;
    public loading$: BehaviorSubject<boolean>;
    public subscription: Subscription;
    public relation$: Observable<RelationInterface>;

    constructor(
        private store$: Store<fromRoot.State>,
        private insurancePolicyDataService: InsurancePolicyDataService,
        private insurancePolicyLoadingService: InsurancePolicyLoadingService,
        private insurancePolicyActionService: InsurancePolicyActionService,
        private relationDataService: RelationDataService,
        private route: ActivatedRoute,
        private datalayerService: DatalayerService,
    ) {
        this.relation$ = this.relationDataService.getRelation$();
        this.total$ = this.insurancePolicyDataService.getTotal$();
        this.purchasedProducts$ = this.insurancePolicyDataService
            .getPurchasedProducts$()
            .pipe(tap((policy) => this.trackPartner(policy)));
        this.loading$ = new BehaviorSubject(true);
        this.subscription = new Subscription();
    }

    public ngOnInit(): void {
        this.setBreadcrumb();
        this.loadPurchasedProducts();
        this.loadScenarios();
    }

    public ngOnDestroy(): void {
        this.store$.dispatch(new productPanelActions.Reset());
        this.subscription.unsubscribe();
    }

    private trackPartner(products: PurchasedProductInterface[]): void {
        const legacyProducts: PolicyProductInterface[] = products
            .map((product) => product?.legacyResponse?.purchased_products)
            .flat();

        if (legacyProducts?.length > 0) {
            this.datalayerService.trackPartner(legacyProducts);
        }
    }

    private loadPurchasedProducts(): void {
        const getStrategy = this.insurancePolicyLoadingService
            .getStrategy$(InsurancePolicyLoadingStrategyEnum.Default)
            .subscribe({
                next: () => this.onLoadPurchasedProductsSuccess(),
                error: (errors: ErrorInterface[]) => this.onLoadPurchasedProductsError(errors),
            });

        this.subscription.add(getStrategy);
    }

    private onLoadPurchasedProductsSuccess(): void {
        this.validateQueryParams();
        this.loading$.next(false);
    }

    private onLoadPurchasedProductsError(errors: ErrorInterface[]): void {
        this.loading$.next(false);
        console.error(errors);
    }

    private setBreadcrumb(): void {
        this.store$.dispatch(
            new layoutActions.SetBreadcrumb([BreadcrumbService.HOME, { title: 'Mijn Verzekeringsmap' }]),
        );
    }

    private validateQueryParams(): void {
        this.route.queryParams
            .pipe(
                filter((params: InsurancesQueryParamsInterface) =>
                    Boolean(params.productSequenceNumber && params.productId && params.panel),
                ),
            )
            .subscribe({
                next: (params) => this.handleQueryParams(params),
            });
    }

    private handleQueryParams(params: InsurancesQueryParamsInterface): void {
        if (
            params.panel === SelectedPanelEnum.Close ||
            params.panel === SelectedPanelEnum.InternationalMotorInsuranceCard
        ) {
            return;
        }

        const productDetails = this.insurancePolicyActionService
            .productDetails$({
                productId: params.productId,
                productSequenceNumber: params.productSequenceNumber,
            })
            .subscribe();

        this.subscription.add(productDetails);
    }

    // TODO: WIDL-1140 add scenarios from data library
    private loadScenarios(): void {
        this.store$.dispatch(new scenariosActions.Load());
    }
}
